import { FileSearch, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { useScrollFade } from "../../hooks/useScrollFade";
import { cn } from "../../lib/utils";
import AggregatorValidationTab from "../audit/AggregatorValidationTab";
import PreApprovalChangeHistoryTab from "../audit/PreApprovalChangeHistoryTab";
import StatusHistoryTab from "../audit/StatusHistoryTab";
import ViewAllNotesTab from "../audit/ViewAllNotesTab";
import FabButton from "./FabButton";
import { AUDIT_TABS, type AuditTabId } from "./mockData";

export const INTERNAL_AUDIT_PANEL_ID = "internal-audit-panel";

function scrollTabIntoView(event: React.MouseEvent<HTMLButtonElement>) {
  const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  event.currentTarget.scrollIntoView({
    behavior: prefersReducedMotion ? "auto" : "smooth",
    inline: "start",
    block: "nearest",
  });
}

type InternalAuditDrawerProperties = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The deal / approval whose audit data the drawer shows. */
  approvalId: string;
  /** Tab to jump to when the drawer opens; defaults to the first tab when omitted. */
  initialTabId?: AuditTabId;
  canViewPreApprovalHistory?: boolean;
};

function InternalAuditDrawer({
  open,
  onOpenChange,
  approvalId,
  initialTabId,
  canViewPreApprovalHistory = false,
}: InternalAuditDrawerProperties) {
  const { t } = useTranslation();
  const tabs = AUDIT_TABS.filter((tab) => tab.id !== "pre-approval-history" || canViewPreApprovalHistory);
  const firstTabId = tabs[0].id;
  const requestedTabId = tabs.find((tab) => tab.id === initialTabId)?.id ?? firstTabId;
  const { scrollReference, startSentinelReference, endSentinelReference, showStartFade, showEndFade } = useScrollFade();
  const [activeTabId, setActiveTabId] = useState<AuditTabId>(requestedTabId);

  // The Sheet's portal content unmounts while closed, but this component
  // instance doesn't — resync to the requested tab on every open so a caller
  // (e.g. DealStatus's history links) can jump straight to a specific tab.
  useEffect(() => {
    if (open) {
      setActiveTabId(requestedTabId);
    }
  }, [open, requestedTabId]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/*
       * SheetTrigger is not used here because Radix Dialog/Sheet's trigger
       * and FAB are separate concerns in the cluster. The FAB button controls
       * open state through the cluster's onOpenChange prop instead.
       */}
      <FabButton
        label={t("actions.internalAudit")}
        icon={<FileSearch className="size-6" />}
        variant="purple"
        expanded={open}
        controls={INTERNAL_AUDIT_PANEL_ID}
        hasPopup="dialog"
        onClick={() => onOpenChange(!open)}
      />
      <SheetContent id={INTERNAL_AUDIT_PANEL_ID} side="right" className="w-full p-0 sm:max-w-4xl">
        <SheetHeader className="px-(--spacing-6) py-(--spacing-3) border-(--color-border-default-base) bg-(--color-background-default-muted) flex-row items-center justify-between border-b">
          <SheetTitle>{t("actions.internalAudit")}</SheetTitle>
          <SheetDescription className="sr-only">{t("actions.internalAuditDescription")}</SheetDescription>
          <SheetClose
            className="text-muted-foreground hover:text-foreground rounded-sm transition-colors"
            aria-label={t("actions.closeInternalAudit")}
          >
            <X className="size-5" aria-hidden="true" />
          </SheetClose>
        </SheetHeader>

        <Tabs
          value={activeTabId}
          onValueChange={(value) => setActiveTabId(value as AuditTabId)}
          className="flex flex-1 flex-col overflow-hidden"
        >
          <div className="relative">
            <TabsList ref={scrollReference} className="no-scrollbar h-auto w-full justify-start overflow-x-auto">
              <div ref={startSentinelReference} aria-hidden="true" className="w-px shrink-0 self-stretch" />
              {tabs.map((tab) => (
                <TabsTrigger key={tab.id} value={tab.id} onClick={scrollTabIntoView}>
                  {tab.label}
                </TabsTrigger>
              ))}
              <div ref={endSentinelReference} aria-hidden="true" className="w-px shrink-0 self-stretch" />
            </TabsList>

            {/* Extra shadows to indicate scrollable content */}
            <div
              aria-hidden="true"
              className={cn(
                "from-(--color-background-default-muted) pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r to-transparent transition-opacity duration-200",
                showStartFade ? "opacity-100" : "opacity-0"
              )}
            />
            <div
              aria-hidden="true"
              className={cn(
                "from-(--color-background-default-muted) pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l to-transparent transition-opacity duration-200",
                showEndFade ? "opacity-100" : "opacity-0"
              )}
            />
          </div>

          {tabs.map((tab) => (
            <TabsContent key={tab.id} value={tab.id} className="p-(--spacing-6) min-h-0 flex-1 overflow-y-auto">
              {tab.id === "view-all-notes" ? (
                <ViewAllNotesTab approvalId={approvalId} />
              ) : tab.id === "pre-approval-history" ? (
                <PreApprovalChangeHistoryTab approvalId={approvalId} />
              ) : tab.id === "status-history" ? (
                <StatusHistoryTab approvalId={approvalId} />
              ) : tab.id === "aggregator-validation" ? (
                <AggregatorValidationTab approvalId={approvalId} />
              ) : (
                <div className="text-muted-foreground flex h-32 items-center justify-center rounded-lg border border-dashed text-sm">
                  {tab.label} — placeholder
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </SheetContent>
    </Sheet>
  );
}

export default InternalAuditDrawer;
